import type { AdvisorsPaths } from '@src/components/sco/Advisors/paths'
import type { DirCategory } from '@src/components/sco/Advisors/types'
import { ADVISE_TOPIC_TO_CATEGORY_KEY } from '@src/components/sco/Advisors/utils/categoryKeys'

/** L10: a chip with fewer matches than this is hidden. */
export const LANDING_CHIP_MIN_MATCHES = 3
export const LANDING_CATEGORY_CHIPS = 5
export const LANDING_SKILL_CHIPS = 0
export const LANDING_CARD_COUNT = 8

export type LandingChip =
  | { kind: 'category'; key: string; label: string; count: number }
  | { kind: 'skill'; key: string; label: string; count: number }

/** Top categories by approved-advisor count (ties keep `DIR_CATEGORIES` order); hides < 3. */
export const pickCategoryChips = (
  categories: Pick<DirCategory, 'key' | 'name'>[],
  countByKey: Record<string, number | null | undefined>,
  limit = LANDING_CATEGORY_CHIPS
): LandingChip[] =>
  categories
    .map((cat, index) => ({ cat, index, count: Number(countByKey[cat.key] ?? 0) }))
    .filter(row => row.count >= LANDING_CHIP_MIN_MATCHES)
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .slice(0, limit)
    .map(({ cat, count }) => ({ kind: 'category' as const, key: cat.key, label: cat.name, count }))

const normalizeLabel = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * Skill-chip candidates: the most-used skill tags across the sample, most-used first.
 * Skips blanks and tags that are just a category label (apply writes category titles as skills),
 * so skill chips never duplicate category chips. Case-insensitive; keeps the most common spelling.
 */
export const pickSkillCandidates = (
  skillTagLists: Array<Array<string | null> | null | undefined>,
  categories: Pick<DirCategory, 'key' | 'name'>[],
  limit: number
): string[] => {
  const categoryLabels = new Set<string>([
    ...categories.map(cat => normalizeLabel(cat.name)),
    ...Object.keys(ADVISE_TOPIC_TO_CATEGORY_KEY).map(normalizeLabel),
  ])
  const counts = new Map<string, { count: number; spellings: Map<string, number>; firstSeen: number }>()
  let seen = 0

  for (const list of skillTagLists) {
    // One vote per advisor per skill.
    const unique = new Map<string, string>()
    for (const raw of list ?? []) {
      const label = (raw ?? '').trim().replace(/\s+/g, ' ')
      const key = normalizeLabel(label)
      if (!key || categoryLabels.has(key) || unique.has(key)) {
        continue
      }
      unique.set(key, label)
    }
    for (const [key, label] of unique) {
      const row = counts.get(key) ?? { count: 0, spellings: new Map(), firstSeen: seen++ }
      row.count += 1
      row.spellings.set(label, (row.spellings.get(label) ?? 0) + 1)
      counts.set(key, row)
    }
  }

  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.firstSeen - b.firstSeen)
    .slice(0, limit)
    .map(row => [...row.spellings.entries()].sort((a, b) => b[1] - a[1])[0][0])
}

/** Skill chips from directory keyword-search counts, in candidate order; hides < 3. */
export const pickSkillChips = (
  candidates: string[],
  countByIndex: Array<number | null | undefined>,
  limit = LANDING_SKILL_CHIPS
): LandingChip[] =>
  candidates
    .map((label, index) => ({ label, count: Number(countByIndex[index] ?? 0) }))
    .filter(row => row.count >= LANDING_CHIP_MIN_MATCHES)
    .slice(0, limit)
    .map(row => ({ kind: 'skill' as const, key: row.label, label: row.label, count: row.count }))

/** "See all" carries the selected filter into the directory (`?category=` or `?q=`). */
export const landingSeeAllHref = (
  paths: Pick<AdvisorsPaths, 'directory' | 'withQuery'>,
  chip: LandingChip | null
): string => {
  if (!chip) {
    return paths.directory
  }
  return chip.kind === 'category'
    ? paths.withQuery(paths.directory, { category: chip.key })
    : paths.withQuery(paths.directory, { q: chip.key })
}
